import { apiClient } from './client'
import type {
  CreateInventoryItemRequest,
  FollowingDetail,
  FollowingSummary,
  InventoryItem,
  LinkInventoryRequest,
  RefreshDiff,
  UpdateInventoryItemRequest,
} from './types'

// ---- Start / followings -----------------------------------------------------

interface StartProjectResponse {
  projectId: string
  startedAt: string
}

export const startProject = async (projectId: string): Promise<StartProjectResponse> => {
  const response = await apiClient.post<StartProjectResponse>(`/api/v1/projects/${projectId}/start`)
  return response.data
}

export const listFollowings = async (): Promise<FollowingSummary[]> => {
  const response = await apiClient.get<FollowingSummary[]>('/api/v1/me/followings')
  return response.data
}

export const getFollowingDetail = async (projectId: string): Promise<FollowingDetail> => {
  const response = await apiClient.get<FollowingDetail>(`/api/v1/me/followings/${projectId}`)
  return response.data
}

export const abandonFollowing = async (projectId: string): Promise<void> => {
  await apiClient.delete(`/api/v1/me/followings/${projectId}`)
}

export const setStepProgress = async (
  projectId: string,
  stepId: string,
  completed: boolean
): Promise<void> => {
  await apiClient.patch(`/api/v1/me/followings/${projectId}/steps/${stepId}/progress`, {
    completed,
  })
}

// ---- Refresh ---------------------------------------------------------------

/**
 * Two-phase refresh: preview returns the diff without mutating; confirm replaces the snapshot
 * and bumps source_version. The dialog calls preview first, then confirm if the user agrees.
 */
export const previewRefresh = async (projectId: string): Promise<RefreshDiff> => {
  const response = await apiClient.post<RefreshDiff>(
    `/api/v1/me/followings/${projectId}/refresh`,
    {}
  )
  return response.data
}

export const confirmRefresh = async (projectId: string): Promise<RefreshDiff> => {
  const response = await apiClient.post<RefreshDiff>(`/api/v1/me/followings/${projectId}/refresh`, {
    confirm: true,
  })
  return response.data
}

// ---- Inventory -------------------------------------------------------------

export const listInventory = async (): Promise<InventoryItem[]> => {
  const response = await apiClient.get<InventoryItem[]>('/api/v1/me/inventory')
  return response.data
}

export const createInventoryItem = async (
  data: CreateInventoryItemRequest
): Promise<InventoryItem> => {
  const response = await apiClient.post<InventoryItem>('/api/v1/me/inventory', data)
  return response.data
}

export const updateInventoryItem = async (
  id: string,
  data: UpdateInventoryItemRequest
): Promise<InventoryItem> => {
  const response = await apiClient.patch<InventoryItem>(`/api/v1/me/inventory/${id}`, data)
  return response.data
}

export const deleteInventoryItem = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/v1/me/inventory/${id}`)
}

export const linkInventoryToGlobal = async (
  id: string,
  request: LinkInventoryRequest
): Promise<InventoryItem> => {
  const response = await apiClient.patch<InventoryItem>(`/api/v1/me/inventory/${id}/link`, request)
  return response.data
}
